import { useVersionCheck } from '@/hooks/useVersionCheck';
import { useRouteChangeRefresh } from '@/hooks/useRouteChangeRefresh';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface UpdateNotificationProps {
  /** Se true, faz refresh automático ao mudar de rota */
  autoRefreshOnRouteChange?: boolean;
}

export function UpdateNotification({ 
  autoRefreshOnRouteChange = true 
}: UpdateNotificationProps) {
  const { 
    hasUpdate, 
    currentVersion, 
    latestVersion, 
    refresh, 
    dismissUpdate,
    isChecking 
  } = useVersionCheck();

  // Hook para auto-refresh em navegação
  useRouteChangeRefresh({ 
    hasUpdate, 
    enabled: autoRefreshOnRouteChange 
  });

  // Não mostra nada se não há update
  if (!hasUpdate) {
    return null;
  }

  return (
    <div 
      className={cn(
        "fixed top-0 left-0 right-0 z-[9999]",
        "bg-accent backdrop-blur-sm",
        "text-accent-foreground",
        "py-2 px-4",
        "flex items-center justify-center gap-3",
        "animate-in slide-in-from-top duration-300",
        "shadow-lg border-b border-border"
      )}
    >
      <RefreshCw 
        className={cn(
          "h-4 w-4 flex-shrink-0",
          isChecking && "animate-spin"
        )} 
      />
      
      <span className="text-sm font-medium">
        Nova versão disponível!
      </span>
      
      <Button
        size="sm"
        variant="secondary"
        onClick={refresh}
        className="h-7 px-3 text-xs font-semibold"
      >
        Atualizar agora
      </Button>
      
      <button
        onClick={dismissUpdate}
        className="ml-2 p-1 hover:bg-muted rounded transition-colors"
        title="Lembrar mais tarde"
        aria-label="Fechar notificação"
      >
        <X className="h-4 w-4" />
      </button>
      
      {/* Debug info (só em desenvolvimento) */}
      {import.meta.env.DEV && currentVersion && latestVersion && (
        <span className="text-xs opacity-60 ml-2">
          {currentVersion.slice(0, 6)} → {latestVersion.slice(0, 6)}
        </span>
      )}
    </div>
  );
}
